import { type Row } from '@tanstack/react-table';
import { MoreVerticalIcon, TrashIcon } from 'lucide-react';
import { useContext, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import {
  MerchantOrderStatusEnum,
  type OpsOrder,
} from '@/graphql/generatedTypes';
import {
  ViewerContext,
  CanAccessPartnerData,
} from '@/providers/ViewerContextProvider';
import { canTransitionTo } from '../../utils/orderTransitionRules';
import {
  SHIPPING_LABEL_ELIGIBLE_STATUSES,
  SHIPPING_LABEL_VIEWABLE_STATUSES,
} from '../../utils/labelStatuses';
import { useOrderMutations } from './OrderMutationsContext';
import { toast } from 'sonner';
import { buildOrderFormUrl } from '@/views/Order/utils/buildOrderFormUrl';
import i18n from '@/utils/i18n';
import SvgIcon from '@/components/Icon/SvgIcon';

interface UnifiedRowActionsProps {
  row: Row<OpsOrder>;
  variant: 'order' | 'event';
}

export function UnifiedRowActions({ row, variant }: UnifiedRowActionsProps) {
  const showStatusTransitions = variant === 'order';
  const showCopyLink = variant === 'event';
  const {
    onCancel,
    onUpdateStatus,
    onRefetchOrderDocuments,
    onRefetchDeliveryNote,
  } = useOrderMutations();
  const viewer = useContext(ViewerContext);
  const canAccessPartnerData = CanAccessPartnerData(viewer);
  const {
    id: orderId,
    status,
    system,
    order_form_id,
    type: orderType,
  } = row.original;
  const canCancel = canTransitionTo(
    system,
    status,
    MerchantOrderStatusEnum.Canceled,
    orderType,
  );
  const canRefetchOrderDocuments =
    variant === 'order' &&
    onRefetchOrderDocuments &&
    SHIPPING_LABEL_ELIGIBLE_STATUSES.has(status);
  // The documents exist once the order is sent to picking, but must not be
  // viewable until it has actually been picked - keep the action greyed out
  // until then.
  const orderDocumentsViewable = SHIPPING_LABEL_VIEWABLE_STATUSES.has(status);
  // When the normal order-documents action is not available (e.g. the order was
  // cancelled) partner-data users still get a dedicated delivery-note action.
  // Gated on the same role set as colugo's `canAccessPartnerData`
  // (utils/MiddleWares/Admin.js) so the two cannot drift apart.
  const canRefetchDeliveryNote =
    variant === 'order' &&
    canAccessPartnerData &&
    !SHIPPING_LABEL_ELIGIBLE_STATUSES.has(status);
  const showSeparatorBeforeCancel =
    canCancel && (showCopyLink || showStatusTransitions);
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);

  const runRowAction = async (action: () => Promise<void>) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setIsLoading(true);
    action().finally(() => {
      isLoadingRef.current = false;
      setIsLoading(false);
    });
  };

  const handleCopyLink = async () => {
    if (!order_form_id) {
      toast.error(i18n.t('Order:toastNoFormLink'));
      return;
    }
    const fullLink = buildOrderFormUrl(order_form_id);
    try {
      await navigator.clipboard.writeText(fullLink);
      toast.success(i18n.t('Common:linkCopied'), {
        action: {
          label: i18n.t('Common:openInNewTab'),
          onClick: () => window.open(fullLink, '_blank'),
        },
      });
    } catch {
      toast.error(i18n.t('Common:copyLinkError'));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="tw:size-9"
          disabled={isLoading}
        >
          <span className="tw:sr-only">Open menu</span>
          {isLoading ? <Spinner /> : <MoreVerticalIcon />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {showCopyLink && (
          <DropdownMenuItem
            onClick={() => void runRowAction(handleCopyLink)}
            disabled={isLoading}
          >
            <SvgIcon name="copy-content" className="tw:size-4" />
            {i18n.t('Event:copyOrderFormLink')}
          </DropdownMenuItem>
        )}
        {showStatusTransitions && (
          <>
            <DropdownMenuItem
              disabled={
                isLoading ||
                !canTransitionTo(
                  system,
                  status,
                  MerchantOrderStatusEnum.OnHold,
                  orderType,
                )
              }
              onClick={() =>
                void runRowAction(() =>
                  onUpdateStatus([orderId], MerchantOrderStatusEnum.OnHold),
                )
              }
            >
              {i18n.t('Order:setStatusOnHold')}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={
                isLoading ||
                !canTransitionTo(
                  system,
                  status,
                  MerchantOrderStatusEnum.Approved,
                  orderType,
                )
              }
              onClick={() =>
                void runRowAction(() =>
                  onUpdateStatus([orderId], MerchantOrderStatusEnum.Approved),
                )
              }
            >
              {i18n.t('Order:setStatusApproved')}
            </DropdownMenuItem>
          </>
        )}
        {canRefetchOrderDocuments && (
          <DropdownMenuItem
            disabled={isLoading || !orderDocumentsViewable}
            onClick={() =>
              void runRowAction(() => onRefetchOrderDocuments([orderId]))
            }
          >
            {i18n.t('Order:viewOrderDocuments')}
          </DropdownMenuItem>
        )}
        {canRefetchDeliveryNote && onRefetchDeliveryNote && (
          <DropdownMenuItem
            disabled={isLoading}
            onClick={() =>
              void runRowAction(() => onRefetchDeliveryNote([orderId]))
            }
          >
            {i18n.t('Order:downloadDeliveryNote')}
          </DropdownMenuItem>
        )}
        {showSeparatorBeforeCancel && <DropdownMenuSeparator />}
        {canCancel && (
          <DropdownMenuItem
            variant="destructive"
            onClick={() => void runRowAction(() => onCancel([orderId]))}
            disabled={isLoading}
          >
            <TrashIcon className="tw:size-4" />
            {i18n.t('Order:cancelOrder')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Pre-configured row actions for Open System orders */
export function OrderRowActions({ row }: { row: Row<OpsOrder> }) {
  return <UnifiedRowActions row={row} variant="order" />;
}

/** Pre-configured row actions for Event orders */
export function EventRowActions({ row }: { row: Row<OpsOrder> }) {
  return <UnifiedRowActions row={row} variant="event" />;
}
